import { useBlocCoordinator } from "@lib/bloc/hooks"
import { Strings } from "@lib/Strings"
import { safeParseFloat } from "@lib/TypeUtil"
import { Task } from "@model/Task"
import {
    serviceItemEmptyFieldLabel,
    ServiceItemForm,
    serviceItemHasCostValue,
    serviceItemLaborTimeLabel,
    serviceItemTotalCost,
    serviceItemsTotals,
} from "@model/serviceRequests/ServiceItemForm"
import { labelForWorkOrderStatus, WorkOrder } from "@model/workOrders/WorkOrder"
import { WorkRequestStatus, WorkRequestType } from "@model/workRequests/WorkRequest"
import { Button, ButtonType } from "@ui/common/buttons/Button"
import { FormField, FormFieldDirection, renderReadonly } from "@ui/common/form/FormField"
import { TextInput, TextInputType } from "@ui/common/form/TextInput"
import { Grid, GridCont, GridGap } from "@ui/common/grids/Grid"
import { LoadingIndicator, LoadingIndicatorContainer } from "@ui/common/LoadingIndicator"
import { MechanicCard } from "@ui/common/MechanicCard"
import {
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalHeaderSubTitle,
    ModalHeaderTitle,
    ModalSize,
} from "@ui/common/Modal"
import { DI } from "@ui/DI"
import { WorkHistoryOverlayViewModel } from "@ui/workOrders/WorkHistoryOverlayViewModel"
import { WorkOrderNumber } from "@ui/workOrders/WorkOrderNumber"
import { Divider } from "@material-ui/core"
import React from "react"
import { WorkHistoryEntry } from "@state/workOrders/WorkHistoryOverlayBloc"

/**
 * Action Item #6766: Work History Overlay.
 *
 * Opened from the "Work History" link on the Create Work Order form. Lists the closed work
 * orders of the asset on the left and shows the selected one as a read-only Work Order form on
 * the right. Nothing in here can be edited, saved, deleted or uploaded - the overlay has to be
 * closed before the Create Work Order form can be used again.
 */
export function WorkHistoryOverlay(): JSX.Element {
    const [state, viewModel] = useBlocCoordinator(() => DI.workHistoryOverlayViewModel())

    return (
        <Modal
            isVisible={state.isVisible}
            useContentTag={true}
            size={ModalSize.Full}
            className="work-history-overlay-modal"
            onClose={() => viewModel.onCloseClicked()}
        >
            <ModalContent>
                <ModalHeader>
                    <ModalHeaderTitle>Work History</ModalHeaderTitle>
                    <ModalHeaderSubTitle>
                        Closed work orders for this asset. This view is read-only.
                    </ModalHeaderSubTitle>
                </ModalHeader>
                <ModalBody>
                    <LoadingIndicatorContainer>
                        <LoadingIndicator isLoading={state.isLoading} />
                        <div className="work-history-overlay">
                            <div className="work-history-overlay--columns">
                                <div className="work-history-overlay--left">
                                    <div className="work-history-overlay--search">
                                        <TextInput
                                            type={TextInputType.Search}
                                            value={state.searchText}
                                            placeholder="Search work orders and service items"
                                            onChange={(value) => viewModel.searchTextChanged(value)}
                                        />
                                    </div>
                                    <div className="work-history-overlay--list">
                                    {state.hasDataFetchError ? (
                                        <div className="work-history-overlay--empty">
                                            <p>There was an error loading the work history.</p>
                                            <Button
                                                label="Retry"
                                                type={ButtonType.Outlined}
                                                onClick={() => viewModel.retryClicked()}
                                            />
                                        </div>
                                    ) : state.filteredEntries.length === 0 ? (
                                        <div className="work-history-overlay--empty">
                                            <p>
                                                {state.isLoading
                                                    ? "Loading closed work orders..."
                                                    : state.entries.length === 0
                                                    ? "This asset has no closed work orders."
                                                    : "No closed work orders match your search."}
                                            </p>
                                        </div>
                                    ) : (
                                        state.filteredEntries.map((entry) => (
                                            <WorkHistoryListItem
                                                key={entry.workOrder.id}
                                                entry={entry}
                                                isSelected={entry.workOrder.id === state.selectedWorkOrderId}
                                                viewModel={viewModel}
                                                onClick={() => viewModel.workOrderSelected(entry.workOrder.id)}
                                            />
                                        ))
                                    )}
                                    </div>
                                </div>
                                <div className="work-history-overlay--details">
                                    {state.selectedEntry ? (
                                        <WorkHistoryWorkOrderDetails
                                            entry={state.selectedEntry}
                                            viewModel={viewModel}
                                        />
                                    ) : (
                                        <div className="work-history-overlay--empty">
                                            <p>Select a work order to view its details.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </LoadingIndicatorContainer>
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

function WorkHistoryListItem({
    entry,
    isSelected,
    viewModel,
    onClick,
}: {
    entry: WorkHistoryEntry
    isSelected: boolean
    viewModel: WorkHistoryOverlayViewModel
    onClick: () => void
}): JSX.Element {
    const workOrder = entry.workOrder

    return (
        <div
            className={`work-history-list-item${isSelected ? " work-history-list-item--selected" : ""}`}
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") onClick()
            }}
        >
            <div className="work-history-list-item--header">
                <span className="work-order-number">Work Order {workOrder.number ?? "-"}</span>
                {/* Date only: the timestamps of the bulk work order fetch are not reliable to the second */}
                <span className="closed-date">{viewModel.formatDate(workOrder.closedDate) || "-"}</span>
            </div>
            {/* Complaint/Instructions is truncated with ellipses when it does not fit */}
            <div className="work-history-list-item--instructions" title={workOrder.specialInstructions}>
                {workOrder.specialInstructions || "-"}
            </div>
            <div className="work-history-list-item--footer">
                <div className="detail">
                    <span className="label">Assigned To</span>
                    <span className="value">{viewModel.contactLabel(workOrder.assignedTo) || "Unassigned"}</span>
                </div>
                <div className="detail">
                    <span className="label">Total Cost</span>
                    <span className="value">{viewModel.formatMoney(entry.totalCost)}</span>
                </div>
                <div className="detail">
                    <span className="label">Service Items</span>
                    <span className="value">{entry.serviceItemCount}</span>
                </div>
            </div>
        </div>
    )
}

/**
 * Read-only copy of the Create Work Order form. The asset selection and the asset details
 * section are intentionally left out, and every action that would write data (save, print,
 * delete, reassign, attachments) is removed rather than disabled.
 */
function WorkHistoryWorkOrderDetails({
    entry,
    viewModel,
}: {
    entry: WorkHistoryEntry
    viewModel: WorkHistoryOverlayViewModel
}): JSX.Element {
    const workOrder = entry.workOrder
    const totals = serviceItemsTotals(entry.serviceItems)

    return (
        <GridCont gap={GridGap.Small} className="work-history-overlay--work-order">
            <Grid columns={1}>
                <FormField label="Work Order Details"></FormField>
            </Grid>
            <Divider></Divider>
            <Grid columns={3}>
                <FormField label="Work Order Status">
                    {renderReadonly(labelForWorkOrderStatus(workOrder.status))}
                </FormField>
                <FormField label="Work Order Number">
                    <WorkOrderNumber
                        assetId={workOrder.assetId}
                        id={workOrder.id}
                        number={workOrder.number}
                        isLink={false}
                    />
                </FormField>
                <FormField label="Closed Date & Time">
                    {renderReadonly(viewModel.formatDateTime(workOrder.closedDate))}
                </FormField>
            </Grid>
            <Grid columns={3}>
                <CreatedByField workOrder={workOrder} viewModel={viewModel} />
                <MechanicCard label="Work Assigned To" mechanic={viewModel.getContactById(workOrder.assignedTo)} />
                <FormField label="Work Performed By" direction={FormFieldDirection.Column}>
                    {renderReadonly(viewModel.workPerformedByLabel(workOrder))}
                </FormField>
            </Grid>
            <Grid columns={4}>
                <FormField label="Work Order Due Date" direction={FormFieldDirection.Column}>
                    {renderReadonly(viewModel.formatDate(workOrder.dueDate))}
                </FormField>
                <FormField label="Due Hour Meter" direction={FormFieldDirection.Column}>
                    {renderReadonly(
                        workOrder.dueHourMeter != null ? Strings.formatDecimal(workOrder.dueHourMeter) : ""
                    )}
                </FormField>
                <FormField label="Due Odometer" direction={FormFieldDirection.Column}>
                    {renderReadonly(workOrder.dueOdometer != null ? Strings.formatInteger(workOrder.dueOdometer) : "")}
                </FormField>
                <FormField label="Site">{renderReadonly(viewModel.siteName(workOrder))}</FormField>
            </Grid>
            <Grid columns={4}>
                <FormField label="Completion Date & Time" direction={FormFieldDirection.Column}>
                    {renderReadonly(viewModel.formatDateTime(workOrder.workCompletedDate))}
                </FormField>
                <FormField label="Physical Hour Meter" direction={FormFieldDirection.Column}>
                    {renderReadonly(
                        workOrder.completedHourMeter != null ? Strings.formatDecimal(workOrder.completedHourMeter) : ""
                    )}
                </FormField>
                <FormField label="Physical Odometer" direction={FormFieldDirection.Column}>
                    {renderReadonly(
                        workOrder.completedOdometer != null ? Strings.formatInteger(workOrder.completedOdometer) : ""
                    )}
                </FormField>
                <div></div>
            </Grid>
            <Grid columns={1}>
                <FormField label="Description" direction={FormFieldDirection.Column}>
                    {renderReadonly(workOrder.specialInstructions)}
                </FormField>
            </Grid>
            <Grid columns={1}>
                <div className="service-items-container">
                    <div className="service-items-header">
                        <div>
                            <div className="title">
                                Service Items <span className="count">{entry.serviceItemCount}</span>
                            </div>
                            <div className="subtitle">Services and tasks performed on this work order</div>
                        </div>
                        {entry.serviceItemCount > 0 && (
                            <div className="totals-box">
                                <div className="total-block">
                                    Labor <strong>{viewModel.formatMoney(totals.labor)}</strong>
                                </div>
                                <div className="total-block">
                                    Parts <strong>{viewModel.formatMoney(totals.parts)}</strong>
                                </div>
                                <div className="total-block">
                                    Misc <strong>{viewModel.formatMoney(totals.misc)}</strong>
                                </div>
                                <div className="total-block grand-total">
                                    Total <strong>{viewModel.formatMoney(totals.total)}</strong>
                                </div>
                            </div>
                        )}
                    </div>
                    {entry.serviceItemCount === 0 ? (
                        <div className="service-items-empty">
                            <p className="empty-title">No service items on this work order</p>
                        </div>
                    ) : (
                        entry.serviceItems.map((item) => (
                            <WorkHistoryServiceItem key={item.uiElementId} item={item} viewModel={viewModel} />
                        ))
                    )}
                </div>
            </Grid>
        </GridCont>
    )
}

function CreatedByField({
    workOrder,
    viewModel,
}: {
    workOrder: WorkOrder
    viewModel: WorkHistoryOverlayViewModel
}): JSX.Element {
    const contact = viewModel.createdByContact(workOrder)
    if (contact) return <MechanicCard label="Created By" mechanic={contact} />

    return (
        <FormField label="Created By" direction={FormFieldDirection.Row}>
            <div style={{ minHeight: "43px", marginLeft: "10px" }}>
                <div style={{ fontWeight: "bold", fontSize: "14px" }}>{viewModel.createdByLabel(workOrder)}</div>
            </div>
        </FormField>
    )
}

/**
 * Read-only rendering of a service item, mirroring the saved/collapsed service item card.
 * Collapsed by default; the card can be expanded to view all its values, which stay read-only.
 */
function WorkHistoryServiceItem({
    item,
    viewModel,
}: {
    item: ServiceItemForm
    viewModel: WorkHistoryOverlayViewModel
}): JSX.Element {
    const [isExpanded, setIsExpanded] = React.useState(false)
    // Action Item 10811: these fields have no default value, so a field the user left empty reads as
    // "-" instead of 0h 0m / $0.00.
    const costLabel = (cost: string | null) =>
        serviceItemHasCostValue(cost) ? viewModel.formatMoney(safeParseFloat(cost)) : serviceItemEmptyFieldLabel
    const isDeclined = !!(item.dismissedReason && item.dismissedReason.trim() !== "")
    const isScheduled =
        item.workRequestType === WorkRequestType.Preventative || item.workRequestType === WorkRequestType.Inspection
    const tasks: Task[] = [...(item.tasks ?? [])].sort((a, b) => a.label.localeCompare(b.label))

    return (
        <div className="service-item-card">
            <div className="card-header readonly-header">
                <div className="left">
                    <div className="service-id">SERVICE #: {viewModel.serviceItemNumber(item)}</div>
                    <div className="service-name">{item.name || "Service Item"}</div>
                </div>
                <div className="center">
                    {isScheduled ? (
                        <div className="due-info">
                            <div className="due-block">
                                <div className="label">Due Hours</div>
                                <div className="value">{item.dueHourMeter ?? "-"}</div>
                            </div>
                            <div className="due-block">
                                <div className="label">Due Miles</div>
                                <div className="value">
                                    {item.dueOdometer != null ? item.dueOdometer.toLocaleString() : "-"}
                                </div>
                            </div>
                            <div className="due-block">
                                <div className="label">Due Date</div>
                                <div className="value">{viewModel.formatDate(item.dueDate) || "-"}</div>
                            </div>
                        </div>
                    ) : (
                        <div className="status">
                            <div className="status-block">
                                Labor Time
                                <strong>{serviceItemLaborTimeLabel(item)}</strong>
                            </div>
                            <div className="status-block">
                                Total Cost
                                <strong>{viewModel.formatMoney(serviceItemTotalCost(item))}</strong>
                            </div>
                        </div>
                    )}
                </div>
                <div className="right">
                    <Button
                        label={isExpanded ? "Hide Item" : "View Item"}
                        type={ButtonType.Outlined}
                        onClick={() => setIsExpanded(!isExpanded)}
                    />
                </div>
            </div>
            {item.status === WorkRequestStatus.Done && (
                <div className="completed-banner" role="status">
                    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M20.285 6.709l-11.025 11.025-5.546-5.547 1.414-1.414 4.132 4.132 9.611-9.611z" />
                    </svg>
                    <div className="content">
                        <div className="title">WORK COMPLETED</div>
                    </div>
                </div>
            )}
            {isDeclined && (
                <div className="declined-banner" role="status">
                    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 2L1 21h22L12 2zm0 14a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm-1-9h2v7h-2V7z" />
                    </svg>
                    <div className="content">
                        <div className="title">WORK DECLINED</div>
                        <div className="reason">{item.dismissedReason}</div>
                    </div>
                </div>
            )}
            {isExpanded && (
            <div className="readonly-body service-item-readonly">
                <div className="row">
                    <div className="col">
                        <strong>Service Code:</strong> {viewModel.serviceCodeLabel(item) || "-"}
                    </div>
                    <div className="col notes">
                        <strong>Mechanics Notes:</strong>{" "}
                        {item.notes && item.notes.trim().length > 0 ? item.notes : "-"}
                    </div>
                    <div className="col total">
                        <strong>Total Cost:</strong> {viewModel.formatMoney(serviceItemTotalCost(item))}
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <strong>Actual Labor Hours:</strong> {serviceItemLaborTimeLabel(item)}
                    </div>
                    <div className="col">
                        <strong>Actual Labor Cost:</strong> {costLabel(item.laborCost)}
                    </div>
                    <div className="col">
                        <strong>Actual Parts Cost:</strong> {costLabel(item.partsCost)}
                    </div>
                    <div className="col">
                        <strong>Misc Cost:</strong> {costLabel(item.otherCost)}
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <strong>Attachments:</strong>{" "}
                        {item.attachments.length > 0 ? `${item.attachments.length} file(s) attached` : "None"}
                    </div>
                </div>
                {isScheduled && tasks.length > 0 && (
                    <div className="work-history-task-list">
                        <div className="work-history-task-list--title">Tasks to be Performed</div>
                        {tasks.map((task) => (
                            <div className="work-history-task-list--row" key={task.id}>
                                <span className="task-label">{task.label}</span>
                                <span className="task-value">{task.value}</span>
                                <span className="task-measurement">{task.measurement ?? ""}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            )}
        </div>
    )
}
